
import { NavLink } from 'react-router-dom';
import RallyCard from '@/components/ui-custom/RallyCard';
import { useFeaturedRally } from '@/hooks/useFeaturedRally';
import { Calendar, MapPin, Users } from 'lucide-react';

const RallyRegistrationSection = () => {
  const { rally, loading, error } = useFeaturedRally();

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Extract location from meeting_location array
  const getLocation = (location: [number, number]) => {
    // This is a placeholder - in a real app you'd reverse geocode or store location name
    return "Location TBD";
  };

  if (loading) {
    return (
      <section className="section-padding bg-adv-green/10 border-y border-adv-darkGray/20">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10">
            <div>
              <h2 className="heading-lg text-adv-dark mb-2">Upcoming Rally</h2>
              <p className="text-adv-darkGray">Loading rally information...</p>
            </div>
          </div>
          <div className="animate-pulse">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-gray-200 h-64 rounded-lg"></div>
              </div>
              <div className="bg-gray-200 h-64 rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !rally) {
    // No featured rallies - show placeholder
    return (
      <section className="section-padding bg-adv-green/10 border-y border-adv-darkGray/20">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10">
            <div>
              <h2 className="heading-lg text-adv-dark mb-2">Upcoming Rally</h2>
              <p className="text-adv-darkGray">We're working on bringing you amazing rally experiences.</p>
            </div>
            <NavLink to="/rallies" className="btn-primary mt-4 md:mt-0">
              Learn More
            </NavLink>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="glass-card rounded-lg p-6 md:p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-adv-orange" />
                  </div>
                  <h3 className="heading-sm text-adv-dark mb-2">Rally Organization Coming Soon</h3>
                  <p className="text-adv-darkGray mb-4">
                    We're excited to announce that rally organization will be opening to our community members in the future. 
                    Stay tuned for updates on how you can create and host your own adventure motorcycle rallies.
                  </p>
                  <div className="flex items-center justify-center space-x-4 text-sm text-adv-darkGray">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span>Multiple Locations</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      <span>Community Driven</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="glass-card rounded-lg p-6 md:p-8 flex flex-col justify-between">
              <div>
                <h3 className="heading-sm text-adv-dark mb-4">Future Rally Features</h3>
                
                <div className="space-y-6">
                  <div className="rounded-lg bg-adv-sand/80 p-4 border border-adv-darkGray/20">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-adv-dark">Community Hosted</h4>
                      <span className="text-adv-orange font-bold">Coming Soon</span>
                    </div>
                    <p className="text-sm text-adv-darkGray">
                      Members will be able to create and host their own rally events.
                    </p>
                  </div>
                  
                  <div className="rounded-lg bg-adv-orange/10 p-4 border border-adv-orange/30">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-adv-dark">Revenue Sharing</h4>
                      <span className="text-adv-orange font-bold">Platform Support</span>
                    </div>
                    <p className="text-sm text-adv-darkGray">
                      Hosts will receive a percentage of ticket sales and platform support.
                    </p>
                  </div>
                </div>
              </div>
              
              <NavLink to="/rallies" className="btn-primary w-full text-center mt-6">
                Learn More
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Render actual rally data
  return (
    <section className="section-padding bg-adv-green/10 border-y border-adv-darkGray/20">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10">
          <div>
            <h2 className="heading-lg text-adv-dark mb-2">Upcoming Rally</h2>
            <p className="text-adv-darkGray">Secure your spot for our next adventure gathering.</p>
          </div>
          <NavLink to="/rallies" className="btn-primary mt-4 md:mt-0">
            View All Rallies
          </NavLink>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RallyCard
              id={rally.id}
              title={rally.title}
              date={formatDate(rally.meeting_datetime)}
              location={getLocation(rally.meeting_location)}
              image={rally.image_url || "/images/rally-placeholder.jpg"}
              attendees={0} // This would come from registration count in a real app
              featured={true}
            />
          </div>
          
          <div className="glass-card rounded-lg p-6 md:p-8 flex flex-col justify-between">
            <div>
              <h3 className="heading-sm text-adv-dark mb-4">Registration Options</h3>
              
              <div className="space-y-6">
                {rally.ticket_tiers?.map((tier, index) => (
                  <div 
                    key={index}
                    className={`rounded-lg p-4 border ${
                      index === 0 
                        ? 'bg-adv-sand/80 border-adv-darkGray/20' 
                        : 'bg-adv-orange/10 border-adv-orange/30'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-adv-dark">{tier.name}</h4>
                      <span className="text-adv-orange font-bold">${tier.price}</span>
                    </div>
                    <p className="text-sm text-adv-darkGray">
                      {tier.description}
                    </p>
                  </div>
                )) || (
                  <>
                    <div className="rounded-lg bg-adv-sand/80 p-4 border border-adv-darkGray/20">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-adv-dark">Base: BYOG</h4>
                        <span className="text-adv-orange font-bold">$250</span>
                      </div>
                      <p className="text-sm text-adv-darkGray">
                        Bring your own gear. Includes access to all rides, events, and meals.
                      </p>
                    </div>
                    
                    <div className="rounded-lg bg-adv-orange/10 p-4 border border-adv-orange/30">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-adv-dark">Premium: Camp Ready</h4>
                        <span className="text-adv-orange font-bold">$450</span>
                      </div>
                      <p className="text-sm text-adv-darkGray">
                        Everything set up for you. Premium tent, sleeping pad, and VIP perks.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <NavLink to={`/rallies/${rally.id}`} className="btn-primary w-full text-center mt-6">
              Register Now
            </NavLink>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RallyRegistrationSection;
